import { Link } from 'react-router-dom';

export default function SwissCheeseModelPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold text-gray-900">
              PharmaBeast
            </Link>
            <div className="flex items-center gap-8">
              <Link to="/theory" className="text-gray-700 hover:text-gray-900 whitespace-nowrap cursor-pointer">
                理論一覧
              </Link>
              <Link to="/services" className="text-gray-700 hover:text-gray-900 whitespace-nowrap cursor-pointer">
                サービス
              </Link>
              <Link to="/beast-blog" className="text-gray-700 hover:text-gray-900 whitespace-nowrap cursor-pointer">
                ブログ
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-600 via-rose-500 to-pink-500 text-white py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
            医療安全の世界標準理論
          </div>
          <h1 className="text-5xl font-bold mb-6">
            スイスチーズ・モデル
          </h1>
          <p className="text-2xl mb-8 font-light">
            誤薬は「注意力」ではなく「構造」で防げる
          </p>
          <p className="text-xl opacity-90">
            ― ヒューマンエラーは、個人の問題ではない ―
          </p>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <i className="ri-lightbulb-flash-line text-3xl"></i>
              結論
            </h2>
            <p className="text-lg leading-relaxed">
              誤薬は、「気をつければ防げる」ものではありません。防げるかどうかは、最初から<strong>"構造で決まっている"</strong>のです。
            </p>
          </div>
        </div>
      </section>

      {/* Theory Explanation */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">理論の解説｜スイスチーズ・モデルとは何か</h2>
          
          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-8 rounded-2xl mb-8">
            <p className="text-lg text-gray-800 mb-6 leading-relaxed">
              スイスチーズ・モデルは、心理学者 <strong>ジェームズ・リーズン</strong>が提唱した、医療安全・航空安全の世界標準理論です。
            </p>
            <div className="bg-white p-6 rounded-xl border-l-4 border-red-500">
              <p className="text-lg font-semibold text-gray-900 mb-3">この理論の核心は、非常に明快です。</p>
              <p className="text-gray-800 leading-relaxed">
                事故は、1つのミスで起きるのではない。<br />
                複数の防御層にある"穴"が、偶然一直線に並んだときに起きる。
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg">
            <p className="text-gray-800 font-semibold">
              ここで重要なのは、<span className="text-red-600">「穴」は必ず存在する</span>、という前提です。
            </p>
          </div>
        </div>
      </section>

      {/* Two Types of Holes */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">「穴」には2種類ある</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-orange-500">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <i className="ri-eye-off-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">① アクティブ・フェイリア</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4 font-semibold">（顕在エラー）</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-orange-500 mt-1"></i>
                  <span className="text-gray-700">見落とし</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-orange-500 mt-1"></i>
                  <span className="text-gray-700">勘違い</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-orange-500 mt-1"></i>
                  <span className="text-gray-700">確認漏れ</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-orange-500 mt-1"></i>
                  <span className="text-gray-700">うっかり</span>
                </li>
              </ul>
              <p className="mt-4 text-sm text-gray-600">多くの事故報告は、ここで止まります。</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-red-600">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <i className="ri-alert-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">② レイテンント・コンディション</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4 font-semibold">（潜在的欠陥）</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-500 mt-1"></i>
                  <span className="text-gray-700">分かりにくい処方表記</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-500 mt-1"></i>
                  <span className="text-gray-700">類似薬が隣に並んだ棚</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-500 mt-1"></i>
                  <span className="text-gray-700">鑑査が割り込まれやすい動線</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-500 mt-1"></i>
                  <span className="text-gray-700">忙しすぎるシフト</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-500 mt-1"></i>
                  <span className="text-gray-700">「急いで」が当たり前の文化</span>
                </li>
              </ul>
              <div className="mt-4 bg-red-50 p-3 rounded-lg">
                <p className="text-red-700 font-bold">👉 本当に危険なのは、こちらです。</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-6 rounded-xl">
            <p className="text-lg font-semibold text-center">
              スイスチーズ・モデルは、個人ではなく"システム"を見よと教えています。
            </p>
          </div>
        </div>
      </section>

      {/* Common Misconception */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">調剤薬局で起きている典型的な誤解</h2>
          
          <div className="bg-gray-50 p-8 rounded-2xl mb-6">
            <p className="text-lg text-gray-800 mb-6">多くの薬局で、事故後にこう言われます。</p>
            <div className="space-y-3 mb-6">
              <div className="bg-white p-4 rounded-lg border-l-4 border-gray-400">
                <p className="text-gray-700">「もっと注意すればよかった」</p>
              </div>
              <div className="bg-white p-4 rounded-lg border-l-4 border-gray-400">
                <p className="text-gray-700">「確認を徹底しよう」</p>
              </div>
              <div className="bg-white p-4 rounded-lg border-l-4 border-gray-400">
                <p className="text-gray-700">「次から気をつけよう」</p>
              </div>
            </div>
            <div className="bg-red-50 p-6 rounded-xl border-l-4 border-red-500">
              <p className="text-lg font-bold text-red-700 mb-3">しかし、これは再発防止ではありません。</p>
              <p className="text-gray-800">理由は単純です。</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white p-6 rounded-xl">
            <p className="text-lg font-bold">
              👉 注意力は、忙しさ・疲労・時間帯で必ず低下するからです。
            </p>
          </div>
        </div>
      </section>

      {/* 5 Layers of Defense */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">調剤薬局への翻訳｜誤薬は5層で止める</h2>
          <p className="text-lg text-gray-700 mb-8">
            スイスチーズ・モデルを薬局業務に翻訳すると「多重防御」になります。
          </p>
          
          <div className="space-y-6">
            {/* Layer 1 */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-blue-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-blue-600">①</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">層① 受付・入力</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-blue-500 mt-1"></i>
                      <span className="text-gray-700">「分1／分3」「朝／毎食後」など→ 高リスク用法に強制注意表示</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-blue-500 mt-1"></i>
                      <span className="text-gray-700">入力時点で"疑う仕組み"を作る</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Layer 2 */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-indigo-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-indigo-600">②</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">層② 処方監査</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-indigo-500 mt-1"></i>
                      <span className="text-gray-700">「分3＝毎食後」という思い込みを外す</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-indigo-500 mt-1"></i>
                      <span className="text-gray-700">患者の生活背景（食事回数・施設配薬）を確認項目に固定</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Layer 3 */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-purple-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-purple-600">③</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">層③ 調剤・ピッキング</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-purple-500 mt-1"></i>
                      <span className="text-gray-700">類似薬・規格違いは→ 棚配置を分ける／色・形状ラベル</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Layer 4 */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-pink-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-pink-600">④</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">層④ 鑑査（人＋システム）</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-pink-500 mt-1"></i>
                      <span className="text-gray-700">鑑査支援システムのアラートは→ 重要度で階層化（全部赤は、全部無視される）</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Layer 5 */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-red-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-red-600">⑤</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">層⑤ 投薬（対人確認）</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-red-500 mt-1"></i>
                      <span className="text-gray-700">患者・施設職員に復唱してもらう</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-red-500 mt-1"></i>
                      <span className="text-gray-700">例：「これは朝だけ？毎食後？」</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-gradient-to-r from-red-600 to-rose-600 text-white p-6 rounded-xl">
            <p className="text-lg font-bold text-center">
              👉 人に頼る防御は、最後の層に置く。
            </p>
          </div>
        </div>
      </section>

      {/* Safety and Efficiency */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">安全と効率は「相互依存」</h2>
          
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-2xl mb-6">
            <p className="text-lg text-gray-800 mb-6">ここで重要な視点があります。</p>
            <div className="bg-white p-6 rounded-xl border-l-4 border-amber-500 mb-6">
              <p className="text-gray-800 font-semibold">
                待ち時間が長くなるほど、患者の集中力は落ちる。
              </p>
            </div>
            <div className="bg-amber-100 p-6 rounded-xl mb-6">
              <p className="text-gray-800 mb-3">つまり、</p>
              <p className="text-gray-800 font-semibold">
                待ち時間増加 → 投薬時の聞き漏らし → 新しい"穴"が生まれる
              </p>
            </div>
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6 rounded-xl">
              <p className="text-lg font-bold">
                👉 安全と効率はトレードオフではない。リトルの法則やTOCとセットで考える必要があります。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* KPI */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">KPI｜安全を「構造」で測る</h2>
          <p className="text-lg text-gray-700 mb-8">
            スイスチーズ・モデルでは、事故件数だけを見ません。
          </p>
          
          <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">見るべき指標</h3>
            
            <div className="space-y-6">
              <div className="border-l-4 border-blue-500 pl-6">
                <h4 className="font-bold text-gray-900 mb-3">インシデント分類</h4>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">用法用量</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">薬剤選択</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">規格</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">日数</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">患者取り違え</span>
                </div>
              </div>

              <div className="border-l-4 border-green-500 pl-6">
                <h4 className="font-bold text-gray-900 mb-3">"どこで止まったか"</h4>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <i className="ri-shield-check-line text-green-500"></i>
                    <span className="text-gray-700">初期監査で止めた件数</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-shield-check-line text-green-500"></i>
                    <span className="text-gray-700">最終鑑査で止めた件数</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-shield-check-line text-green-500"></i>
                    <span className="text-gray-700">投薬で気づいた件数</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 rounded-xl">
            <p className="text-lg font-bold text-center">
              👉 上流で止まるほど、防御構造は健全。
            </p>
          </div>
        </div>
      </section>

      {/* Tomorrow's Action */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">明日の一手｜最小で効く改善</h2>
          
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-8 rounded-2xl">
            <p className="text-lg text-gray-800 mb-6">明日やることは、1つで十分です。</p>
            
            <div className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-8 rounded-xl mb-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-chat-check-line text-3xl"></i>
                </div>
                <p className="text-2xl font-bold">
                  投薬時に「復唱」を必須にする。
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl">
              <p className="text-gray-800 mb-4 font-semibold">これは、</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-teal-50 p-4 rounded-lg text-center">
                  <i className="ri-money-dollar-circle-line text-3xl text-teal-600 mb-2"></i>
                  <p className="font-semibold text-gray-900">コストゼロ</p>
                </div>
                <div className="bg-cyan-50 p-4 rounded-lg text-center">
                  <i className="ri-book-open-line text-3xl text-cyan-600 mb-2"></i>
                  <p className="font-semibold text-gray-900">教育不要</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <i className="ri-flashlight-line text-3xl text-blue-600 mb-2"></i>
                  <p className="font-semibold text-gray-900">即日導入可能</p>
                </div>
              </div>
              <p className="text-gray-800 mt-6 text-center font-semibold">
                で、防御層を1枚増やせます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Theories */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">関連する理論</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Link 
              to="/theory/toc" 
              className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer border-t-4 border-red-500"
            >
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-links-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">TOC（制約理論）</h3>
              <p className="text-gray-600 text-sm">全体を決める制約を見つける</p>
            </Link>

            <Link 
              to="/theory/flow-efficiency" 
              className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer border-t-4 border-teal-500"
            >
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-water-flash-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">フロー効率</h3>
              <p className="text-gray-600 text-sm">待ち時間を減らす</p>
            </Link>

            <Link 
              to="/theory" 
              className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer border-t-4 border-gray-500"
            >
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-book-2-line text-2xl text-gray-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">理論一覧</h3>
              <p className="text-gray-600 text-sm">すべての理論を見る</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 to-slate-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">PharmaBeast</h3>
              <p className="text-gray-400 text-sm">
                薬局経営を科学する
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">理論</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/theory/toc" className="text-gray-400 hover:text-white cursor-pointer">
                    TOC（制約理論）
                  </Link>
                </li>
                <li>
                  <Link to="/theory/dbr" className="text-gray-400 hover:text-white cursor-pointer">
                    DBR
                  </Link>
                </li>
                <li>
                  <Link to="/theory/flow-efficiency" className="text-gray-400 hover:text-white cursor-pointer">
                    フロー効率
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">サービス</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/services" className="text-gray-400 hover:text-white cursor-pointer">
                    サービス一覧
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white cursor-pointer">
                    お問い合わせ
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">情報</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/beast-blog" className="text-gray-400 hover:text-white cursor-pointer">
                    ブログ
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="text-gray-400 hover:text-white cursor-pointer">
                    プライバシーポリシー
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 PharmaBeast. All rights reserved. | <a href="https://readdy.ai/?ref=logo" className="hover:text-white cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
